import React, { useCallback, useMemo, useState } from "react";
import { Button } from  "../../common/button/Button";
import { InputTextBox } from "../../common/input/InputTextBox";
import { UserAccountRoleSelector, UserAccountRoleSelectorProps } from "./select/UserAccountRoleSelector";
import { CareRecipientFilterComponent } from "./check/CareRecipientFilterComponent";
import { ViewableCareRecipientCheckComponent } from "./check/ViewableCareRecipientCheckComponent";
import { UserEditType } from "../../../../domain/user/User";
import { UserName, UserNameMaxLength } from "../../../../domain/user/UserName";
import { UserEmail, UserEmailMaxLength } from "../../../../domain/user/UserEmail";
import { UserPassword, PasswordMinLength } from "../../../../domain/user/UserPassword";
import { getLabelFromUserAccountRole, UserAccountRole } from "../../../../domain/user/account/UserAccountRole";
import { CareRecipientWithDataSyncInformation } from "../../../../domain/care_recipient/CareRecipientWithDataSyncInformation";
import { InputCheckBox } from "../../common/input/InputCheckBox";

export const UserInfoEditComponent = React.memo(function UserInfoEditComponent(props: UserInfoEditComponentProps) {
    const {
        editType,
        userName,
        userEmail,
        userRole,
        careRecipientList,
        whenSaveButtonClicked,
    } = props;

    const [name, setName] = useState(userName?.value ?? "");

    const [email, setEmail] = useState(userEmail?.value ?? "");

    const [password, setPassword] = useState("");

    const [role, setRole] = useState(userRole?.toString() ?? "");

    const [houseIdPrefixFilters, setHouseIdPrefixFilters] = useState<string[]>([]);

    const [careRecipients, setCareRecipients] = useState<string[]>([]);

    const [nameErrorMessage, setNameErrorMessage] = useState("");

    const [emailErrorMessage, setEmailErrorMessage] = useState("");

    const [passwordErrorMessage, setPasswordErrorMessage] = useState("");

    const [roleErrorMessage, setRoleErrorMessage] = useState("");

    const [checkedSendEmail, setCheckedSendEmail] = useState(true);

    const handleUserNameChanged = useCallback((value: string) => setName(value), []);

    const handleUserEmailChanged = useCallback((value: string) => setEmail(value), []);

    const handleUserPasswordChanged = useCallback((value: string) => setPassword(value), []);

    const handleHouseIdPrefixFilters = useCallback((houseIdPrefix: string) => 
        setHouseIdPrefixFilters(prevFilters => (prevFilters.includes(houseIdPrefix) ? prevFilters.filter(f => f !== houseIdPrefix) : [...prevFilters, houseIdPrefix]))
    , []);

    const handleCareRecipientChanged = useCallback((newList: string[]) => setCareRecipients(newList), []);

    const userAccountRoleSelectorProps = useMemo<UserAccountRoleSelectorProps>(() => ({
        whenUserAccountRoleChanged: (value: string) => setRole(value),
        hasError: !!roleErrorMessage.length
    }), [roleErrorMessage]);

    const displayCareRecipientList = useMemo(() => {
        // careRecipientList未取得の場合、空のリストを返す
        if (!careRecipientList) return [];

        // フィルター未設定の場合、careRecipientListをそのまま返す
        if (!houseIdPrefixFilters.length) return careRecipientList;

        // careRecipientList取得済 && フィルター設定済の場合、careRecipientListのhouseIdでフィルター条件に当てはまるものを返す
        return careRecipientList.filter(it => (
            houseIdPrefixFilters.filter(houseIdPrefix => it.careRecipient.houseId?.value.startsWith(houseIdPrefix)).length
        ));
    }, [careRecipientList, houseIdPrefixFilters]);

    const isAdd = useMemo(() => editType === UserEditType.ADD, [editType]);

    const isEdit = useMemo(() => editType === UserEditType.UPDATE, [editType]);

    const resetErrors = useCallback(() => {
        setNameErrorMessage("");
        setEmailErrorMessage("");
        setPasswordErrorMessage("");
        setRoleErrorMessage("");
    }, []);

    const handleSaveButtonClicked = useCallback(() => {
        let hasError = false;

        resetErrors();

        // ユーザー名の入力チェック
        if (!name) {
            hasError = true;
            setNameErrorMessage("ユーザー名を入力してください。");
        }
        // ユーザー名の長さチェック（最大20文字）
        if (!UserName.fromString(name).isValidLength()) {
            hasError = true;
            setNameErrorMessage(`ユーザー名は${UserNameMaxLength}文字以内で入力してください。`);
        }

        // メールアドレスのフォーマットチェック
        if (!UserEmail.fromString(email).isValid()
            || !UserEmail.fromString(email).isValidEmailNameLength()
            || !UserEmail.fromString(email).isValidEmailDomainLength()) {
            hasError = true;
            setEmailErrorMessage("メールアドレスの形式が正しくありません。");
        }
        // メールアドレスの長さチェック（最大100文字）
        if (!UserEmail.fromString(email).isValidLength()) {
            hasError = true;
            setEmailErrorMessage(`メールアドレスは${UserEmailMaxLength}文字以内で入力してください。`);
        }

        // パスワードの長さチェック（登録時のみ）（最低12文字）
        if (isAdd && !UserPassword.fromString(password).isValidLength()) {
            hasError = true;
            setPasswordErrorMessage(`パスワードは${PasswordMinLength}文字以上で入力してください。`);
        }

        // ロールの入力チェック
        if (!role) {
            hasError = true;
            setRoleErrorMessage("ロールを選択してください。");
        }

        if (hasError) {
            return;
        }

        // 利用者に1件もチェックしていない場合
        if (careRecipients.length < 1) {
            if (!window.confirm(`利用者が1人も選択されていません。このまま${editType}してよろしいですか？`)) {
                return;
            }
        }

        // 保存
        whenSaveButtonClicked(name, email, password, role, careRecipients, checkedSendEmail);
    }, [isAdd, name, email, password, role, careRecipients, whenSaveButtonClicked, editType, resetErrors, checkedSendEmail]);

    return (
        <div className={"user_edit"}>
            <div className={"content"}>
                <div className="label">ユーザー名</div>
                <InputTextBox value={name} handleTextChanged={handleUserNameChanged} hasError={!!nameErrorMessage}/>
                <div className="error_message">{nameErrorMessage}</div>
            </div>
            <div className={"content"}>
                <div className="label">メールアドレス</div>
                {isAdd && <InputTextBox value={email} handleTextChanged={handleUserEmailChanged} hasError={!!emailErrorMessage.length}/>}
                {isEdit && <div className="email_value">{email}</div>}
                <div className="error_message">{emailErrorMessage}</div>
            </div>
            {isAdd && (
                <div className={"content"}>
                    <div className="label">パスワード</div>
                    <InputTextBox value={password} handleTextChanged={handleUserPasswordChanged} isPassword hasError={!!passwordErrorMessage.length}/>
                    <div className="error_message">{passwordErrorMessage}</div>
                </div>
            )}
            <div className={"content"}>
                <div className="label">ロール</div>
                {isAdd && <UserAccountRoleSelector {...userAccountRoleSelectorProps} />}
                {isEdit && <div className="role_value">{getLabelFromUserAccountRole(userRole!)}</div>}
                <div className="error_message">{roleErrorMessage}</div>
            </div>
            <div className={"content"}>
                <div className="label">利用者</div>
                <CareRecipientFilterComponent houseIdPrefixFilters={houseIdPrefixFilters} handleHouseIdPrefixFilters={handleHouseIdPrefixFilters} />
                <ViewableCareRecipientCheckComponent careRecipientList={displayCareRecipientList} whenCareRecipientChanged={handleCareRecipientChanged} />
            </div>
            { isAdd && <div><InputCheckBox label="登録完了メールを送信する" checked={checkedSendEmail} handleCheckBoxChanged={setCheckedSendEmail}></InputCheckBox></div> }
            <Button label={editType} onClick={handleSaveButtonClicked} />
        </div>
    );
});

export type UserInfoEditComponentProps = {
    editType: UserEditType,
    userName: UserName | null,
    userEmail: UserEmail | null,
    userRole: UserAccountRole | null,
    careRecipientList: CareRecipientWithDataSyncInformation[],
    whenSaveButtonClicked: (name: string, email: string, password: string, role: string, careRecipients: string[], sendEmail: boolean) => void,
};